/**
 * copyUrlPreview — the landing-page URL a click will actually reach.
 *
 * A DELIBERATE PORT of campaign_editor/copy_url_tracking.py's
 * expand_serving_url / apply_final_url_suffix / _duplicate_params. It exists so
 * the step-2 sitelink editor can show the resolved URL and its problems AS YOU
 * TYPE — the feedback whose absence let a live Germany PMax launch ship
 * sitelinks whose UTMs were doubled and whose utm_content was corrupted.
 *
 * TWO IMPLEMENTATIONS OF ONE CONTRACT, which is a real risk: if these rules
 * drift, the preview disagrees with what launches, and a preview you cannot
 * trust is worse than no preview. Three things hold them together:
 *   - the rules are Google's and are short (see the doc links below);
 *   - copyUrlPreview.test.js runs the SAME case table as
 *     campaign-services/tests/test_copy_url_tracking.py, including the exact
 *     issue wording, so a change on either side fails a test on this one;
 *   - the Python remains the authority — it runs at build time and its result
 *     is what the review panel shows for anything the user has not just edited.
 *
 * Google's rules, both documented:
 *   - {lpurl} at the START of a tracking template is inserted UNESCAPED;
 *     anywhere else ? = " # tab ' and space are escaped.
 *     https://developers.google.com/google-ads/api/docs/ads/upgraded-urls/serving-url-rules
 *   - a final URL suffix is query TEXT appended to the landing page URL, with
 *     the connector chosen for you — which is why it is the safe place to tag.
 */

const LPURL_ESCAPES = {
  '?': '%3F', '=': '%3D', '"': '%22', '#': '%23', '\t': '%09', "'": '%27', ' ': '%20',
};

/** Substitute {lpurl} into a tracking template the way Google does. */
export function expandServingUrl(finalUrl, trackingTemplate) {
  const url = finalUrl || '';
  const template = String(trackingTemplate || '').trim();
  if (!template) return { serving: url, issues: [] };

  const issues = [];
  if (!template.includes('{lpurl}') && !template.includes('{unescapedlpurl}')
      && !template.includes('{escapedlpurl}')) {
    // A template with no {lpurl} sends every click to a fixed address. Not this
    // module's job to fix, but previewing it as if the landing page were reached
    // would be a lie.
    issues.push('The tracking template does not contain {lpurl}, so it does not point '
      + "at the final URL — every click goes to the template's own address.");
    return { serving: template, issues };
  }

  let escaped = url;
  for (const [ch, enc] of Object.entries(LPURL_ESCAPES)) escaped = escaped.split(ch).join(enc);

  let serving = template;
  // Only the LEADING occurrence is unescaped, matching the documented rule.
  if (serving.startsWith('{lpurl}')) serving = serving.replace('{lpurl}', url);
  serving = serving.split('{lpurl}').join(escaped);
  serving = serving.split('{unescapedlpurl}').join(url);
  serving = serving.split('{escapedlpurl}').join(escaped);

  // Two "?" is no longer reported — see copy_url_tracking.expand_serving_url. The
  // source campaigns run that shape today and the copy reproduces it on purpose.
  if ((serving.match(/\?/g) || []).length <= 1) {
    const q = serving.indexOf('?');
    const amp = serving.indexOf('&');
    if (amp !== -1 && (q === -1 || amp < q)) {
      issues.push('This produces a URL whose parameters start with "&" instead of "?" — '
        + "the tracking template continues the final URL's query, but this final URL has "
        + 'none, so nothing after the "&" is read as a parameter. Move the tagging to the '
        + 'final URL suffix, which picks the right connector for each URL.');
    }
  }
  return { serving, issues };
}

/** Append a final-URL suffix the way Google does — it picks the connector. */
export function applyFinalUrlSuffix(url, suffix) {
  const text = String(suffix || '').trim().replace(/^[?&]+/, '');
  if (!text) return url;
  return `${url}${url.includes('?') ? '&' : '?'}${text}`;
}

/** Parameter names appearing more than once in the assembled URL, in order. */
export function duplicateParams(url) {
  const query = String(url || '').split('?').slice(1).join('?');
  if (!query) return [];
  const seen = new Set();
  const dupes = [];
  for (const pair of query.split('&')) {
    if (!pair) continue;
    const key = pair.split('=')[0];
    if (seen.has(key) && !dupes.includes(key)) dupes.push(key);
    seen.add(key);
  }
  return dupes;
}

/** The URL a click lands on, plus everything wrong with how it was assembled. */
export function previewLandingUrl(finalUrl, trackingTemplate, finalUrlSuffix) {
  const { serving, issues } = expandServingUrl(finalUrl, trackingTemplate);
  const resolved = applyFinalUrlSuffix(serving, finalUrlSuffix);

  // DUPLICATED PARAMETERS ARE NO LONGER REPORTED — deliberately, and this must stay
  // in step with campaign_editor/copy_url_tracking.preview_landing_url, which made the
  // same change for the same reason.
  //
  // The copier no longer converts a tagging template into a suffix: a copy reproduces
  // its source field for field, and these sources state the same keys in both their
  // template and their sitelinks' own suffixes, so the duplication is inherited rather
  // than caused. Flagging it would fire on every copy of those campaigns for something
  // the copy is not allowed to fix, next to the findings that do need acting on.
  //
  // A query starting with "&" and a template with no {lpurl} are still reported.
  // Two "?" is not — see expandServingUrl.
  //
  // duplicateParams stays exported: correct, tested, and usable as an opt-in signal.
  return { resolved, issues };
}

/**
 * Push a campaign-suffix EDIT down into a sitelink's own suffix.
 *
 * WHY THIS IS NEEDED AT ALL. On the Google side an asset's final URL suffix
 * OVERRIDES the campaign's — it does not add to it. So a sitelink that carries
 * its own never sees a campaign-level change: edit the campaign suffix and every
 * ad picks it up while every tagged sitelink silently keeps the old values.
 *
 * ONLY WHAT CHANGED, which is the whole subtlety and the thing the first version
 * of this got wrong. "Every key the campaign states wins" sounds equivalent and is
 * not, because these campaign suffixes state utm_content themselves:
 *
 *   campaign  …&utm_term={keyword}&utm_content={creative}&utm_network={network}
 *   sitelink  …&utm_content=Best_Exchange_Rates
 *
 * Editing utm_source then dragged utm_content={creative} down with it and replaced
 * every per-link label — the exact damage step 1's old apply-to-every-sitelink box
 * used to do, reintroduced by the fix for a different bug. Diffing prev against
 * next means an untouched key is not propagated at all, so utm_content only ever
 * moves when the user actually edits utm_content.
 *
 * A key REMOVED from the campaign suffix is deliberately NOT removed from the
 * sitelinks: deleting tagging someone may be reporting on is not something to do
 * as a side effect of an unrelated edit. Remove it per-link, where it is visible.
 *
 * Values pass through verbatim — split on "&", never decoded.
 */
export function propagateCampaignSuffixEdit(prevSuffix, nextSuffix, sitelinkSuffix) {
  const pairs = (text) => String(text || '').trim().replace(/^[?&]+/, '')
    .split('&').filter(Boolean)
    .map((part) => [part.split('=')[0], part]);

  const before = new Map(pairs(prevSuffix));
  const after = pairs(nextSuffix);
  // Added, or stated with a different value. An unchanged key is left entirely
  // alone — the sitelink may deliberately disagree with the campaign about it.
  const changed = new Map(after.filter(([k, pair]) => before.get(k) !== pair));
  if (!changed.size) return sitelinkSuffix || '';

  const sitelink = pairs(sitelinkSuffix);
  if (!sitelink.length) return sitelinkSuffix || '';

  const out = sitelink.map(([k, pair]) => (changed.has(k) ? changed.get(k) : pair));
  const seen = new Set(sitelink.map(([k]) => k));
  for (const [k, pair] of changed) if (!seen.has(k)) out.push(pair);
  return out.join('&');
}
